import { action, observable } from 'mobx'
import type { ActivationLifecycle } from '@/infra/activation'
import type { Question } from '@/features/jobs/questions/questions'
import { type QuestionAnswerEditorViewModelBase } from '@/features/jobs/questions/QuestionAnswerEditorViewModelBase'
import { Id } from '@/infra/id'
import type { QuestionValidationResult } from '@/features/jobs/questions/question-validation-result'

/**
 * View-model for questions with editing capabilities.
 */
export class QuestionEditorViewModel<T extends Question = Question>
  implements ActivationLifecycle
{
  /**
   * Question ID.
   */
  @observable accessor id = ''

  /**
   * The question text.
   */
  @observable accessor title = ''

  /**
   * The question description.
   */
  @observable accessor description = ''

  /**
   * Answer editor view model.
   */
  readonly editor

  constructor(editor: QuestionAnswerEditorViewModelBase<T>) {
    this.editor = editor
  }

  /**
   * @inheritDoc
   */
  @action.bound
  activate() {
    const editor = this.editor
    if ('activate' in editor && typeof editor.activate === 'function') {
      editor.activate()
    }
  }

  /**
   * @inheritDoc
   */
  @action.bound
  deactivate() {
    const editor = this.editor
    if ('deactivate' in editor && typeof editor.deactivate === 'function') {
      editor.deactivate()
    }
  }

  /**
   *
   * @param question
   */
  @action.bound
  set(question: T) {
    this.id = Id.assert(question.id)
    this.title = question.title
    this.description = question.description ?? ''
    this.editor.set(question)
  }

  /**
   * Validates the editor's answer.
   */
  @action.bound
  validate(): QuestionValidationResult {
    return this.editor.validate()
  }
}
